import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  RefreshCw,
  MoreHorizontal,
  CalendarDays,
  CircleCheck,
  Stamp,
  Plane,
  ListChecks,
  ArrowUpRight,
} from 'lucide-react';
import { API_URL } from '../../../api/api';
import { wsApi, wsBase } from '../../../pages/Workspace/wsApi';
import { readCachedWeather } from './WeatherWidget';

/* ============================================================
   Daily briefing — one-glance summary of the user's day:
   company events, tasks due, leave approvals waiting on them
   and who is out. Pulls from Calendar, Workspace and Leaves.
   ============================================================ */

const authHeaderFromStorage = () => {
  const token = localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Local YYYY-MM-DD for a DATE value from the API — a bare date string passes
// through; a serialized timestamp is read in local time so IST dates that
// arrive as the previous day's UTC midnight land on the right day.
const dayOf = (v) => {
  if (!v) return null;
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s.replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? null : ymd(d);
};

const firstWord = (name = '') => String(name).trim().split(/\s+/)[0] || '';

const initialsOf = (name = '') =>
  String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || '')
    .join('');

// "Erin is out" / "Erin & Jules are out" / "Erin, Jules & Sam are out" /
// "Erin, Jules & 3 others are out".
const outLine = (names) => {
  if (!names.length) return "Everyone's in today";
  if (names.length === 1) return `${names[0]} is out`;
  if (names.length === 2) return `${names[0]} & ${names[1]} are out`;
  if (names.length === 3) return `${names[0]}, ${names[1]} & ${names[2]} are out`;
  return `${names[0]}, ${names[1]} & ${names.length - 2} others are out`;
};

// "A, B and 2 more" for the summary cards.
const listLine = (items, max = 2) => {
  if (items.length <= max) return items.join(' and ');
  return `${items.slice(0, max).join(', ')} and ${items.length - max} more`;
};

const daysBetween = (a, b) =>
  Math.round((new Date(`${b}T00:00:00`) - new Date(`${a}T00:00:00`)) / 86400000);

const AVATAR_TINTS = ['#fde2e4', '#dbeafe', '#dcfce7', '#ede9fe'];

export default function DailyBriefingWidget() {
  const navigate = useNavigate();
  const role = (localStorage.getItem('role') || 'member').toLowerCase();
  const userData = JSON.parse(localStorage.getItem('userdata') || '{}');
  const me = userData.id;
  const firstName =
    (userData.first_name || '').trim() ||
    firstWord(localStorage.getItem('username')) ||
    'there';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    const auth = authHeaderFromStorage();
    const [ev, tk, lv] = await Promise.allSettled([
      axios.get(`${API_URL}/api/settings/calendar/events`, auth),
      wsApi.myTasks(),
      axios.get(`${API_URL}/api/leaves/all`, auth),
    ]);
    const today = ymd(new Date());

    const events = (ev.status === 'fulfilled' && Array.isArray(ev.value.data) ? ev.value.data : [])
      .map((e) => ({ ...e, day: dayOf(e.event_date) }))
      .filter((e) => e.day && e.day >= today)
      .sort((a, b) => a.day.localeCompare(b.day));

    const tasks = (tk.status === 'fulfilled' && Array.isArray(tk.value) ? tk.value : [])
      .map((t) => ({ ...t, day: dayOf(t.due_date) }))
      .filter((t) => t.status !== 'done' && t.day && t.day <= today)
      .sort((a, b) => a.day.localeCompare(b.day));

    const leaves = lv.status === 'fulfilled' && Array.isArray(lv.value.data) ? lv.value.data : [];
    const approvals = leaves.filter(
      (l) => String(l.status || '').toLowerCase() === 'pending' && l.user_id !== me
    );
    const outByUser = new Map();
    leaves.forEach((l) => {
      if (String(l.status || '').toLowerCase() !== 'approved') return;
      const s = dayOf(l.start_date);
      const e = dayOf(l.end_date);
      if (s && e && s <= today && today <= e) outByUser.set(l.user_id, l);
    });

    setData({
      today,
      eventsToday: events.filter((e) => e.day === today),
      nextEvent: events[0] || null,
      tasks,
      approvals,
      out: [...outByUser.values()],
    });
    setLoading(false);
  }, [me]);

  useEffect(() => {
    load();
  }, [load]);

  // Close the overflow menu on any outside click.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const weather = readCachedWeather();

  const go = (path) => {
    setMenuOpen(false);
    navigate(path);
  };
  const tasksPath = `${wsBase()}/tasks`;
  const leavesPath = `/${role}/leaves`;
  const calendarPath = `/${role}/calendar`;

  const eventsCount = data?.eventsToday.length || 0;
  const tasksCount = data?.tasks.length || 0;
  const approvalsCount = data?.approvals.length || 0;
  const outCount = data?.out.length || 0;
  const dayLoad = eventsCount + tasksCount + approvalsCount;
  const pace = dayLoad === 0
    ? 'Today looks clear:'
    : dayLoad > 6
      ? 'Today is a busy one:'
      : 'Today has a steady pace:';

  const next = data?.nextEvent;
  const nextIn = next ? daysBetween(data.today, next.day) : 0;
  const nextWhen = !next
    ? ''
    : nextIn === 0
      ? 'Today'
      : nextIn === 1
        ? 'Tmrw'
        : new Date(`${next.day}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short' });
  const nextDate = next
    ? new Date(`${next.day}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    : '';

  const outNames = (data?.out || []).map((l) => firstWord(l.employee_name) || 'Someone');

  return (
    <div className="brief">
      <div className="brief-hero">
        <div className="brief-top">
          <span className="brief-kicker">
            <span className="brief-kicker-icon" aria-hidden="true">
              <Sparkles size={13} />
            </span>
            Your daily briefing
          </span>
          <div className="brief-tools" ref={menuRef}>
            <button
              type="button"
              className={`brief-tool${loading ? ' is-spinning' : ''}`}
              onClick={load}
              disabled={loading}
              title="Refresh"
              aria-label="Refresh briefing"
            >
              <RefreshCw size={15} />
            </button>
            <button
              type="button"
              className="brief-tool"
              onClick={() => setMenuOpen((v) => !v)}
              title="More"
              aria-label="More options"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <MoreHorizontal size={16} />
            </button>
            {menuOpen && (
              <div className="brief-menu" role="menu">
                <button type="button" role="menuitem" onClick={() => go(tasksPath)}>Open my tasks</button>
                <button type="button" role="menuitem" onClick={() => go(leavesPath)}>Open leaves</button>
                <button type="button" role="menuitem" onClick={() => go(calendarPath)}>Open calendar</button>
              </div>
            )}
          </div>
        </div>
        <div className="brief-weekday">
          {now.toLocaleDateString('en-US', { weekday: 'long' })}
        </div>
        <div className="brief-dateline">
          <span>{now.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}</span>
          {weather && (
            <>
              <span className="brief-dot" aria-hidden="true" />
              <span className="brief-weather">
                {weather.temp}° · {weather.label.toLowerCase()}
              </span>
            </>
          )}
        </div>
      </div>

      <div className="brief-body">
        <p className="brief-lead">
          {greeting}, {firstName}. {pace}
        </p>
        <div className="brief-chips">
          <span className="brief-chip brief-chip--blue">
            <CalendarDays size={14} /> {eventsCount} {eventsCount === 1 ? 'event' : 'events'}
          </span>
          <span className="brief-chip brief-chip--green">
            <CircleCheck size={14} /> {tasksCount} {tasksCount === 1 ? 'task' : 'tasks'} due
          </span>
          <span className="brief-chip brief-chip--orange">
            <Stamp size={14} /> {approvalsCount} {approvalsCount === 1 ? 'approval' : 'approvals'}
          </span>
          <span className="brief-chip brief-chip--purple">
            <Plane size={14} /> {outCount} {outCount === 1 ? 'person' : 'people'} out
          </span>
        </div>

        <div className="brief-next">
          <div className="brief-next-when">
            <b>{next ? nextWhen : '—'}</b>
            <span>{nextDate}</span>
          </div>
          <div className="brief-next-main">
            <span className="brief-next-kicker">Next up</span>
            <span className="brief-next-title">
              {loading && !data ? 'Loading…' : next ? next.title : 'Nothing scheduled'}
            </span>
            <span className="brief-next-sub">
              {next
                ? [next.event_type, nextIn > 1 ? `in ${nextIn} days` : null].filter(Boolean).join(' · ')
                : 'Your calendar is clear.'}
            </span>
          </div>
          {next && (
            <button type="button" className="brief-next-btn" onClick={() => go(calendarPath)}>
              View
            </button>
          )}
        </div>

        <div className="brief-cards">
          <button type="button" className="brief-card" onClick={() => go(tasksPath)}>
            <span className="brief-card-head">
              <ListChecks size={15} />
              <b>Due today</b>
              <span className="brief-card-count">{tasksCount}</span>
            </span>
            <span className="brief-card-text">
              {tasksCount
                ? `${listLine(data.tasks.map((t) => t.title))}.`
                : "Nothing due — you're all caught up."}
            </span>
          </button>
          <button type="button" className="brief-card" onClick={() => go(leavesPath)}>
            <span className="brief-card-head">
              <Stamp size={15} />
              <b>Waiting on you</b>
              <span className="brief-card-count">{approvalsCount}</span>
            </span>
            <span className="brief-card-text">
              {approvalsCount
                ? `Leave ${approvalsCount === 1 ? 'request' : 'requests'} from ${listLine(
                    data.approvals.map((l) => l.employee_name || 'a teammate')
                  )}.`
                : 'No approvals pending.'}
            </span>
          </button>
        </div>

        <div className="brief-foot">
          <span className="brief-out">
            {outCount > 0 && (
              <span className="brief-avatars" aria-hidden="true">
                {data.out.slice(0, 3).map((l, i) => (
                  <span
                    key={l.user_id || i}
                    className="brief-avatar"
                    style={{ background: AVATAR_TINTS[i % AVATAR_TINTS.length] }}
                  >
                    {initialsOf(l.employee_name)}
                  </span>
                ))}
              </span>
            )}
            <span className="brief-out-text">{outLine(outNames)}</span>
          </span>
          <button type="button" className="brief-link" onClick={() => go(calendarPath)}>
            Open calendar <ArrowUpRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
